
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, List, Users, MoreHorizontal, Edit, Trash, UserPlus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const ScoutLists = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // Mock data - replace with actual Supabase query using scout_athlete_lists and scout_athlete_list_items
  const athleteLists = [
    {
      id: '1',
      name: 'Top Prospects 2025',
      description: 'High priority recruits for the upcoming class',
      athleteCount: 8,
      createdAt: '2024-01-15',
      updatedAt: '2024-01-15',
      athletes: [
        { id: '1', name: 'John Smith', sport: 'Basketball' },
        { id: '2', name: 'Sarah Johnson', sport: 'Soccer' }
      ]
    },
    {
      id: '2',
      name: 'Academic Excellence',
      description: 'Athletes with outstanding academic records',
      athleteCount: 5,
      createdAt: '2024-01-12',
      updatedAt: '2024-01-14',
      athletes: [
        { id: '2', name: 'Sarah Johnson', sport: 'Soccer' },
        { id: '3', name: 'Mike Davis', sport: 'Football' }
      ]
    },
    {
      id: '3',
      name: 'Summer Camp Attendees',
      description: 'Athletes who attended our summer recruiting camp',
      athleteCount: 12,
      createdAt: '2024-01-10',
      updatedAt: '2024-01-12',
      athletes: [
        { id: '1', name: 'John Smith', sport: 'Basketball' },
        { id: '3', name: 'Mike Davis', sport: 'Football' }
      ]
    }
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Athlete Lists</h1>
          <p className="text-muted-foreground">
            Create and manage custom groups of athletes
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create List
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Create New List</DialogTitle>
              <DialogDescription>
                Create a custom list to organize your athletes
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">List Name</Label>
                <Input id="name" placeholder="Enter list name..." />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Textarea 
                  id="description" 
                  placeholder="Describe the purpose of this list..." 
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => setIsCreateDialogOpen(false)}>
                Create List
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {athleteLists.map((list) => (
          <Card key={list.id} className="hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <List className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg">{list.name}</CardTitle>
                  </div>
                  <CardDescription className="text-sm">
                    {list.description}
                  </CardDescription>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit List
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add Athletes
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-destructive">
                      <Trash className="h-4 w-4 mr-2" />
                      Delete List
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{list.athleteCount} athletes</span>
                  </div>
                  <Badge variant="secondary">
                    Updated {new Date(list.updatedAt).toLocaleDateString()}
                  </Badge>
                </div>

                {list.athletes.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Recent Athletes
                    </p>
                    {list.athletes.slice(0, 3).map((athlete) => (
                      <div key={athlete.id} className="flex items-center space-x-2 text-sm">
                        <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-primary">
                            {athlete.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div className="flex-1">
                          <span className="font-medium">{athlete.name}</span>
                          <span className="text-muted-foreground ml-1">• {athlete.sport}</span>
                        </div>
                      </div>
                    ))}
                    {list.athleteCount > 3 && (
                      <p className="text-xs text-muted-foreground">
                        +{list.athleteCount - 3} more athletes
                      </p>
                    )}
                  </div>
                )}

                <div className="pt-2">
                  <Button variant="outline" className="w-full" size="sm">
                    View List
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {athleteLists.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <List className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No lists created</h3>
            <p className="text-muted-foreground">
              Create your first athlete list to organize and group your prospects
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ScoutLists;
